import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { Delete } from 'mdi-material-ui';
import { Button, useDelete, useNotify, useTranslate } from 'react-admin';
import { countCompletedPurchasesForEvent } from '../../services/eventService';
import { getEventDbId } from '../../utils/eventIdentity';

/**
 * Event silme:
 * - Varsayilan: soft delete (panelden gizle, veri kalir)
 * - Admin: kalici silme secenegi (satis yoksa)
 */
const ConditionalDeleteButton = ({ record, resource = 'events-v2', isAdmin = true }) => {
  const translate = useTranslate();
  const [open, setOpen] = useState(false);
  const [checking, setChecking] = useState(false);
  const [purchaseCount, setPurchaseCount] = useState(0);
  const [deleteOne, { isLoading: deleting }] = useDelete();
  const notify = useNotify();

  if (!record?.id) return null;

  const routeKey = record.id;
  const eventDbId = getEventDbId(record);

  const openDialog = async () => {
    setOpen(true);
    setChecking(true);
    try {
      const count = await countCompletedPurchasesForEvent(eventDbId);
      setPurchaseCount(count);
    } catch (error) {
      console.error('Error checking sales for delete:', error);
      notify(translate('show.could_not_verify_sales', 'Could not verify sales for this event.'), { type: 'warning' });
      setPurchaseCount(-1);
    } finally {
      setChecking(false);
    }
  };

  const runDelete = (hard = false) => {
    deleteOne(
      resource,
      { id: routeKey, previousData: record, meta: { hard } },
      {
        onSuccess: () => {
          setOpen(false);
          notify(
            hard
              ? translate('show.event_permanently_deleted', 'Event permanently deleted.')
              : translate('show.event_soft_deleted', 'Event hidden (soft delete).'),
            { type: 'success' },
          );
          window.location.href = '/#/events-v2';
        },
        onError: (error) => {
          notify(error?.message || translate('show.failed_to_delete_event', 'Failed to delete event.'), { type: 'error' });
        },
      },
    );
  };

  const canHardDelete = purchaseCount === 0;

  return (
    <>
      <Button
        startIcon={<Delete />}
        variant="outlined"
        size="large"
        label={translate('show.delete_event', 'Delete Event')}
        onClick={openDialog}
      >
        <Delete />
        &nbsp;<span style={{ fontSize: 12 }}>{translate('show.delete_event', 'Delete Event')}</span>
      </Button>

      <Dialog open={open} onClose={() => !deleting && setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{translate('show.delete_event', 'Delete Event')}</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            {checking ? (
              <span>{translate('show.checking_sales', 'Checking sales...')}</span>
            ) : (
              <>
                <p style={{ marginTop: 0 }}>
                  {translate(
                    'show.delete_event_desc',
                    'Choose how to remove this event. Soft delete keeps all data and hides the event from the panel and website.',
                  )}
                </p>
                {purchaseCount < 0 ? (
                  <p style={{ color: '#b45309' }}>
                    {translate(
                      'show.cannot_verify_sales_warning',
                      'Could not verify sales. Permanent delete is disabled until sales are checked.',
                    )}
                  </p>
                ) : purchaseCount > 0 ? (
                  <p style={{ color: '#b45309', fontWeight: 600 }}>
                    {translate(
                      'show.has_sales_warning',
                      'This event has %{count} completed sale(s). Only soft delete is available.',
                      { count: purchaseCount },
                    )}
                  </p>
                ) : (
                  <p>
                    {translate(
                      'show.no_sales_notice',
                      'No completed ticket sales found. You can soft delete or permanently delete.',
                    )}
                  </p>
                )}
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1, px: 3, pb: 2 }}>
          <Button label={translate('ra.action.cancel', 'Cancel')} onClick={() => setOpen(false)} disabled={deleting} />

          {!checking && (
            <Button
              label={deleting ? translate('show.working', 'Working...') : translate('show.soft_delete', 'Soft Delete')}
              onClick={() => runDelete(false)}
              disabled={deleting}
            />
          )}

          {isAdmin && !checking && canHardDelete && purchaseCount >= 0 && (
            <Button
              label={deleting ? translate('show.working', 'Working...') : translate('show.delete_permanently', 'Delete Permanently')}
              onClick={() => {
                if (
                  window.confirm(
                    translate(
                      'show.hard_delete_confirm',
                      'This will permanently delete the event and its stock data. This cannot be undone. Continue?',
                    ),
                  )
                ) {
                  runDelete(true);
                }
              }}
              disabled={deleting}
              color="error"
            />
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ConditionalDeleteButton;
